import * as React from "react";
import {useEffect, useState} from "react";
import "./CheckoutPage.css";
import {useLocation, useNavigate} from "react-router-dom";
import {TransactionPayinRequestModel} from "../../model/TransactionPayinRequestModel";
import {AuthService} from "../../service/AuthService";
import {toast} from "react-toastify";
import {msgTypes} from "../../constants/msgTypes.js";

import {Box, Button, Radio, RadioGroup, Typography,} from "@mui/material";
import VerifiedUser from "@mui/icons-material/VerifiedUser";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import axios from "axios";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {getPaymentNotice} from "./paymentNotice";
import Loading from "../loader/Loading";

const CheckoutPage = () => {
    const {pathname} = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [paymentNotice, setPaymentNotice] = useState<ReturnType<typeof getPaymentNotice>>(null);
    const [payinRequest, setPayinRequest] = useState(
        new TransactionPayinRequestModel()
    );
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("phonepe");
    const [isPolling, setIsPolling] = useState(false);

    const amount = isNaN(parseFloat(payinRequest.amount))
        ? "0.00"
        : parseFloat(payinRequest.amount).toFixed(2);

    // Fetch payment details on mount
    useEffect(() => {
        const paymentActive = async () => {
            setLoading(true);
            setPaymentNotice(null);
            setIsPolling(false);
            const id = pathname.split("/quickbilpay/")[1];
            try {
                const response = await AuthService.paymentActive({
                    payKey: id,
                });
                const notice = getPaymentNotice(response);
                if (notice) {
                    setPaymentNotice(notice);
                } else if (msgTypes.SUCCESS_CODE.includes(response.statusCode)) {
                    setPayinRequest(response.data.transactionPayinRequest);
                } else {
                    toast.error(response.message || "Failed to load payment details.");
                }
            } catch (error) {
                const notice = axios.isAxiosError(error) ? getPaymentNotice(error.response?.data) : null;
                if (notice) {
                    setPaymentNotice(notice);
                } else {
                    toast.error("An error occurred while fetching payment details.");
                }
            } finally {
                setLoading(false);
            }
        };
        paymentActive();
    }, [pathname]);

    // Poll for payment status when UPI payment is initiated
    useEffect(() => {
        if (paymentNotice || !isPolling || !payinRequest.txnId) {
            return;
        }

        const checkPaymentStatus = async () => {
            try {
                const requestObject = {
                    appId: payinRequest.appId,
                    data: payinRequest.txnId,
                };

                const res = await AuthService.checkStatus(requestObject);

                if (msgTypes.SUCCESS_CODE.includes(res.statusCode)) {
                    if (res.data.status) {
                        const status = res.data.status;

                        if (status !== msgTypes.PENDING) {
                            setIsPolling(false);
                            sessionStorage.removeItem("shouldPollPayment");
                            sessionStorage.removeItem("pollingOrderId");

                            const completeResponse = {
                                ...res.data,
                                returnUrl: payinRequest.returnUrl,
                                custFirstName: payinRequest.custFirstName,
                                custLastName: payinRequest.custLastName,
                                businessName: payinRequest.businessName,
                            };

                            navigate("/response-page", {
                                state: {response: completeResponse},
                            });
                        }
                    }
                }
            } catch (error) {
                console.error("Error checking payment status:", error);
            }
        };

        checkPaymentStatus();
        const interval = setInterval(checkPaymentStatus, 5000);
        return () => clearInterval(interval);
    }, [
        isPolling,
        paymentNotice,
        payinRequest.txnId,
        payinRequest.appId,
        navigate,
        payinRequest.returnUrl,
        payinRequest.custFirstName,
        payinRequest.custLastName,
        payinRequest.businessName,
    ]);

    // Resume polling after redirect
    useEffect(() => {
        const shouldPoll = sessionStorage.getItem("shouldPollPayment");
        const storedOrderId = sessionStorage.getItem("pollingOrderId");

        if (
            !paymentNotice &&
            shouldPoll === "true" &&
            storedOrderId &&
            payinRequest.orderId === storedOrderId
        ) {
            console.log("Resuming polling after redirect");
            setIsPolling(true);
            sessionStorage.removeItem("shouldPollPayment");
        }
    }, [payinRequest.orderId, paymentNotice]);

    // Helper function to convert intent URL for specific apps
    const getAppSpecificUrl = (appName: string) => {
        if (!payinRequest.intentUrl) return "";

        const intentUrl = payinRequest.intentUrl;

        switch (appName) {
            case "phonepe":
                return intentUrl.replace("upi://pay", "phonepe://pay");
            case "paytm":
                return intentUrl.replace("upi://pay", "paytmmp://pay");
            case "gpay":
                return intentUrl.replace("upi://pay", "tez://upi/pay");
            case "anyupi":
                return intentUrl;
            default:
                return intentUrl;
        }
    };

    const handleUpiAppClick = (appName: string) => {
        const appUrl = getAppSpecificUrl(appName);

        console.log("UPI app clicked - storing polling flag");
        sessionStorage.setItem("shouldPollPayment", "true");
        sessionStorage.setItem("pollingOrderId", payinRequest.orderId);

        window.location.href = appUrl;

        // Start polling
        setIsPolling(true);
    };

    const handlePayNow = () => {
        if (!payinRequest.intentUrl) {
            toast.error("Payment method not available.");
            return;
        }

        // Trigger UPI app
        handleUpiAppClick(selectedPaymentMethod);
    };

    const noticeStatus = paymentNotice?.status;
    const NoticeIcon = noticeStatus === "SUCCESS" ? CheckCircleIcon
        : noticeStatus === "PENDING" || noticeStatus === "EXPIRED" ? AccessTimeIcon : CancelIcon;
    const noticeColor = noticeStatus === "SUCCESS" ? "#10b981"
        : noticeStatus === "PENDING" ? "#f59e0b" : "#ef4444";
    const noticeTitle = noticeStatus === "EXPIRED" ? paymentNotice?.message
        : noticeStatus === "SUCCESS" ? "Payment Successful"
        : noticeStatus === "PENDING" ? "Payment Pending" : "Payment Failed";
    const transaction = paymentNotice?.transaction;
    const transactionDetails = transaction ? [
        ["Order ID", transaction.orderId],
        ["Transaction ID", transaction.transactionId],
        ["Amount", transaction.amount != null && Number.isFinite(Number(transaction.amount))
            ? `₹${Number(transaction.amount).toFixed(2)}` : null],
        ["Date", transaction.date],
        ["Customer Name", transaction.customerName],
    ].filter(([, value]) => value != null && value !== "") : [];

    if (loading) return <Loading/>;

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
                background: "#fff",
            }}
        >
            {/* Mobile-like phone container */}
            <Box
                sx={{
                    maxWidth: "420px",
                    width: "100%",
                    height: "100vh",
                    maxHeight: {xs: "100vh", sm: "900px"},
                    background: "#ffffff",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    overflow: "auto",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        background: "#1346b8",
                        padding: "20px 24px",
                        textAlign: "center",
                        borderRadius: "0 0 80px 80px",
                        position: "relative",
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            color: "#ffffff",
                            fontWeight: 700,
                            letterSpacing: "0.5px",
                        }}
                    >
                        Payment
                    </Typography>
                </Box>

                {/* Amount Payable Box */}
                {!paymentNotice && <Box>
                    <Box
                        sx={{
                            background: "#ffffff",
                            borderRadius: "5px",
                            padding: "20px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#000",
                                fontSize: "22px",
                                fontWeight: 700,
                                lineHeight: 1.2,
                            }}
                        >
                            Amount Payable <span style={{color: "#1346b8"}}>₹{amount}</span>
                        </Typography>
                    </Box>
                </Box>}

                {/* Payment Options */}
                <Box
                    sx={{
                        px: 3,
                        pb: 3,
                        flex: 1,
                        mt: 4,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {paymentNotice || !payinRequest.intentUrl ? (
                        // Reuse the existing error layout for bank results and expired payments.
                        <Box
                            role="alert"
                            sx={{
                                textAlign: "center",
                                py: 6,
                                px: 3,
                            }}
                        >
                            {paymentNotice ? <NoticeIcon
                                sx={{fontSize: "80px", color: noticeColor, mb: 3}}
                            /> : <CancelIcon
                                sx={{
                                    fontSize: "80px",
                                    color: "#ef4444",
                                    mb: 3,
                                }}
                            />}
                            <Typography
                                sx={{
                                    color: "#1f2937",
                                    fontSize: "20px",
                                    fontWeight: 700,
                                    mb: 2,
                                }}
                            >
                                {paymentNotice ? noticeTitle : "Payment Method Not Available"}
                            </Typography>
                            <Typography
                                sx={{
                                    color: "#6b7280",
                                    fontSize: "14px",
                                    lineHeight: 1.6,
                                    mb: 3,
                                }}
                            >
                                {paymentNotice
                                    ? noticeStatus === "EXPIRED"
                                        ? "Please contact the merchant for a new payment link."
                                        : paymentNotice.message
                                    : "The payment link is either invalid, expired, or the payment method is currently unavailable. Please contact the merchant for assistance."}
                            </Typography>
                            {noticeStatus === "PENDING" && (
                                <Typography sx={{color: "#6b7280", fontSize: "14px", mb: 3}}>
                                    Please wait for confirmation from your bank before making another payment.
                                </Typography>
                            )}
                            {transactionDetails.length > 0 && (
                                <Box sx={{background: "#f9fafb", borderRadius: "12px", p: 2, textAlign: "left"}}>
                                    {transactionDetails.map(([label, value]) => (
                                        <Box key={label} sx={{mb: 1.5}}>
                                            <Typography sx={{color: "#6b7280", fontSize: "13px"}}>{label}</Typography>
                                            <Typography sx={{color: "#1f2937", fontSize: "14px", fontWeight: 600, overflowWrap: "anywhere"}}>
                                                {value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {!paymentNotice && <Button
                                onClick={() => window.location.reload()}
                                variant="outlined"
                                sx={{
                                    color: "#1346b8",
                                    borderColor: "#1346b8",
                                    textTransform: "none",
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                    "&:hover": {
                                        borderColor: "#0f3a8f",
                                        background: "rgba(19, 70, 184, 0.04)",
                                    },
                                }}
                            >
                                Try Again
                            </Button>}
                        </Box>
                    ) : (
                        <>
                            <RadioGroup
                                value={selectedPaymentMethod}
                                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                            >
                                {/* PhonePe */}
                                <Box
                                    onClick={() => setSelectedPaymentMethod("phonepe")}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: 1.5,
                                        mb: 1.5,
                                        border:
                                            selectedPaymentMethod === "phonepe"
                                                ? "2px solid #5f259f"
                                                : "2px solid #e5e7eb",
                                        borderRadius: "12px",
                                        cursor: "pointer",
                                        transition: "all 0.3s ease",
                                        background:
                                            selectedPaymentMethod === "phonepe"
                                                ? "rgba(95, 37, 159, 0.05)"
                                                : "#ffffff",
                                        "&:hover": {
                                            borderColor: "#5f259f",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 12px rgba(95, 37, 159, 0.15)",
                                        },
                                    }}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                        <img
                                            src="/images/phonepay.png"
                                            alt="PhonePe"
                                            style={{
                                                width: "40px",
                                                height: "40px",
                                                objectFit: "contain",
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "16px",
                                                color: "#1f2937",
                                            }}
                                        >
                                            PhonePe
                                        </Typography>
                                    </Box>
                                    <Radio
                                        value="phonepe"
                                        sx={{
                                            color: "#d1d5db",
                                            "&.Mui-checked": {
                                                color: "#5f259f",
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Paytm */}
                                <Box
                                    onClick={() => setSelectedPaymentMethod("paytm")}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: 1.5,
                                        mb: 1.5,
                                        border:
                                            selectedPaymentMethod === "paytm"
                                                ? "2px solid #00BAF2"
                                                : "2px solid #e5e7eb",
                                        borderRadius: "12px",
                                        cursor: "pointer",
                                        transition: "all 0.3s ease",
                                        background:
                                            selectedPaymentMethod === "paytm"
                                                ? "rgba(0, 186, 242, 0.05)"
                                                : "#ffffff",
                                        "&:hover": {
                                            borderColor: "#00BAF2",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 12px rgba(0, 186, 242, 0.15)",
                                        },
                                    }}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                        <img
                                            src="/images/paytm.png"
                                            alt="Paytm"
                                            style={{
                                                width: "40px",
                                                height: "40px",
                                                objectFit: "contain",
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "16px",
                                                color: "#1f2937",
                                            }}
                                        >
                                            Paytm
                                        </Typography>
                                    </Box>
                                    <Radio
                                        value="paytm"
                                        sx={{
                                            color: "#d1d5db",
                                            "&.Mui-checked": {
                                                color: "#00BAF2",
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Google Pay */}
                                <Box
                                    onClick={() => setSelectedPaymentMethod("gpay")}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: 1.5,
                                        mb: 1.5,
                                        border:
                                            selectedPaymentMethod === "gpay"
                                                ? "2px solid #4285F4"
                                                : "2px solid #e5e7eb",
                                        borderRadius: "12px",
                                        cursor: "pointer",
                                        transition: "all 0.3s ease",
                                        background:
                                            selectedPaymentMethod === "gpay"
                                                ? "rgba(66, 133, 244, 0.05)"
                                                : "#ffffff",
                                        "&:hover": {
                                            borderColor: "#4285F4",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 12px rgba(66, 133, 244, 0.15)",
                                        },
                                    }}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                        <img
                                            src="/images/googlepay.png"
                                            alt="Google Pay"
                                            style={{
                                                width: "40px",
                                                height: "40px",
                                                objectFit: "contain",
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "16px",
                                                color: "#1f2937",
                                            }}
                                        >
                                            Google Pay
                                        </Typography>
                                    </Box>
                                    <Radio
                                        value="gpay"
                                        sx={{
                                            color: "#d1d5db",
                                            "&.Mui-checked": {
                                                color: "#4285F4",
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Pay with any UPI */}
                                <Box
                                    onClick={() => setSelectedPaymentMethod("anyupi")}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: 1.5,
                                        mb: 1.5,
                                        border:
                                            selectedPaymentMethod === "anyupi"
                                                ? "2px solid #097939"
                                                : "2px solid #e5e7eb",
                                        borderRadius: "12px",
                                        cursor: "pointer",
                                        transition: "all 0.3s ease",
                                        background:
                                            selectedPaymentMethod === "anyupi"
                                                ? "rgba(9, 121, 57, 0.05)"
                                                : "#ffffff",
                                        "&:hover": {
                                            borderColor: "#097939",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 12px rgba(9, 121, 57, 0.15)",
                                        },
                                    }}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                        <img
                                            src="/images/upi.png"
                                            alt="UPI"
                                            style={{
                                                width: "40px",
                                                height: "40px",
                                                objectFit: "contain",
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "16px",
                                                color: "#1f2937",
                                            }}
                                        >
                                            Pay with any upi
                                        </Typography>
                                    </Box>
                                    <Radio
                                        value="anyupi"
                                        sx={{
                                            color: "#d1d5db",
                                            "&.Mui-checked": {
                                                color: "#097939",
                                            },
                                        }}
                                    />
                                </Box>
                            </RadioGroup>

                            {/* Security Badge */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    mt: 3,
                                    mb: 3,
                                }}
                            >
                                <VerifiedUser sx={{color: "#10b981", fontSize: "20px"}}/>
                                <Typography
                                    sx={{
                                        color: "#10b981",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                    }}
                                >
                                    100% Secure Payments
                                </Typography>
                            </Box>

                            {/* Pay Button */}
                            <Button
                                onClick={handlePayNow}
                                fullWidth
                                sx={{
                                    background: "#1346b8",
                                    color: "#ffffff",
                                    fontSize: "18px",
                                    fontWeight: 700,
                                    py: 2,
                                    borderRadius: "16px",
                                    textTransform: "none",
                                    boxShadow: "0 8px 20px rgba(79, 70, 229, 0.3)",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        background:
                                            "linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)",
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 12px 28px rgba(79, 70, 229, 0.4)",
                                    },
                                    "&:active": {
                                        transform: "translateY(0)",
                                    },
                                }}
                            >
                                Pay ₹{amount}
                            </Button>

                            {/* Warning Messages */}
                            <Box sx={{mt: 3}}>
                                <Typography
                                    sx={{
                                        color: "#ef4444",
                                        fontSize: "13px",
                                        textAlign: "center",
                                        mb: 1,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    1. Please make sure you have installed the app
                                </Typography>
                                <Typography
                                    sx={{
                                        color: "#ef4444",
                                        fontSize: "13px",
                                        textAlign: "center",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    2. Don't pay for the same link repeatedly
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default CheckoutPage;
